import {
  ActivityTemplate,
  ActivityTemplateUpdate,
  canCopyTemplate,
  canOpenTemplate,
  checkAddTemplateEditor,
  checkCreateTemplate,
  checkRemoveEditor,
  checkRemoveTemplate,
  checkTemplateAddCompletionCriterionToActivityAtIndex,
  checkTemplateAddDocument,
  checkTemplateAddFunction,
  checkTemplateAddLink,
  checkTemplateAppendWorkstream,
  checkTemplateRemoveCCFromActivity,
  checkTemplateRemoveDocument,
  checkTemplateRemoveFunction,
  checkTemplateRemoveLinkTemplate,
  checkTemplateRemoveWorkstream,
  checkTemplateUpdateActivity,
  checkTemplateUpdateCC,
  checkTemplateUpdateDocument,
  checkTemplateUpdateFunction,
  checkTemplateUpdateLink,
  checkTemplateUpdateWorkstream,
  checkUpdateTemplate,
  checkUpdateTemplateDetails,
  checkUpdateTemplateStatus,
  CompletionCriterionTemplateUpdate,
  CreateDocumentTemplateRequest,
  CreateLinkTemplateRequest,
  CreateProjectFunctionRequest,
  CreateProjectMapTemplateRequest,
  doesUserHaveScopes,
  isUserTemplateEditor,
  isUserTemplateEditorOrAbove,
  isTemplateVisible,
  isUserTemplateOwner,
  MergeFunctionRequest,
  ProjectMapTemplate,
  TemplateStatus,
  UpdateDocumentTemplateRequest,
  UpdateLinkTemplateRequest,
  UpdateProjectMapTemplateDetailsRequest,
  UpdateProjectMapTemplateRequest,
  UpdateProjFunctionRequest,
  validateMergeTemplateFunctionRequest,
  WorkstreamTemplateCreate,
  WorkstreamTemplateUpdate,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useProfileStore } from './profileStore';
import { useTemplateStore, type TemplateBasics } from './templateStore';
import { useUserStore } from './userStore';

export const useTemplateRulesStore = defineStore('templateRulesStore', {
  getters: {
    user() {
      return useProfileStore().profile;
    },

    templateLinks() {
      return useTemplateStore().templateLinks;
    },

    hasTemplate() {
      return (templateId: string) => {
        return useTemplateStore().hasTemplate(templateId);
      };
    },

    template() {
      return (templateId: string): ProjectMapTemplate => {
        const template = useTemplateStore().template(templateId);

        return template;
      };
    },

    templateBasics() {
      return (templateId: string): TemplateBasics => {
        const template = useTemplateStore().templateBasics(templateId);
        if (!template) {
          throw new Error('Missing template');
        }

        return template;
      };
    },

    activity() {
      return (templateId: string, activityId: string) => {
        return useTemplateStore().activitySummary(templateId, activityId);
      };
    },

    hasEditors() {
      return (templateId: string) => {
        return useTemplateStore().hasEditors(templateId);
      };
    },

    editorsTemplate() {
      return (templateId: string) => {
        const template = useTemplateStore().editorsTemplate(templateId);

        return template;
      };
    },
  },
  actions: {
    isUserTemplateEditor(templateId: string) {
      if (!this.hasEditors(templateId)) {
        return false;
      }

      return !isUserTemplateEditor(
        this.user.id,
        this.editorsTemplate(templateId),
      ).length;
    },

    isUserTemplateOwner(templateId: string) {
      return !isUserTemplateOwner(this.user.id, this.template(templateId).owner)
        .length;
    },

    isUserTemplateEditorOrAbove(templateId: string) {
      return !isUserTemplateEditorOrAbove(
        this.user.id,
        this.template(templateId).owner,
        this.editorsTemplate(templateId),
      ).length;
    },

    /**
     * False once opening the template would be refused, e.g. for its old owner after a
     * handover (WI #5789). `extraEditorIds` adds editors the store has not loaded, such
     * as an old owner who stayed editor (WI #5774).
     */
    canUserOpenTemplate(templateId: string, extraEditorIds: string[] = []) {
      const template = this.templateBasics(templateId);
      const editorIds = this.hasEditors(templateId)
        ? this.editorsTemplate(templateId).map((editor) => editor.id)
        : [];
      const readerScopes = this.user.scopes.template_reader ?? [];

      return canOpenTemplate(
        {
          status: template.status,
          ownerId: template.owner?.id,
          editorsIds: [...editorIds, ...extraEditorIds],
        },
        this.user.id,
        this.user.roles,
        template.scopes.some((scope) => readerScopes.includes(scope)),
      );
    },

    /**
     * A copy is created like a new template: template creator in every scope of the
     * template (WI #5794). It takes the template as a source, so the template must be
     * visible without MANAGE_TEMPLATE_OWNER (WI #5789); the template list holds only
     * such templates and passes `isVisible`.
     */
    canUserCopyTemplate(templateId: string, isVisible?: boolean) {
      const template = this.templateBasics(templateId);
      if (
        !canCopyTemplate(
          template.scopes,
          this.user.scopes.template_creator ?? [],
        )
      ) {
        return false;
      }
      if (isVisible !== undefined) {
        return isVisible;
      }
      const editorIds = this.hasEditors(templateId)
        ? this.editorsTemplate(templateId).map((editor) => editor.id)
        : [];
      const readerScopes = this.user.scopes.template_reader ?? [];

      return isTemplateVisible(
        {
          status: template.status,
          ownerId: template.owner?.id,
          editorsIds: editorIds,
        },
        this.user.id,
        template.scopes.some((scope) => readerScopes.includes(scope)),
      );
    },

    doesUserHaveScopes() {
      return doesUserHaveScopes(this.user.scopes.template_creator);
    },

    checkCreateTemplate(payload: CreateProjectMapTemplateRequest) {
      return checkCreateTemplate(
        payload,
        this.user.scopes.template_creator,
        this.templateLinks,
      );
    },

    async checkAddTemplateEditor(templateId: string, userIds: string[]) {
      await Promise.all(
        userIds.map((userId) => useUserStore().loadUserProfile(userId)),
      );
      const templateReaderArrays = userIds.map(
        (userId) =>
          useUserStore().userDetails(userId).scopes.template_reader || [],
      );
      const intersection = templateReaderArrays.reduce((acc, curr) =>
        acc.filter((scope) => curr.includes(scope)),
      );

      return checkAddTemplateEditor(
        this.user.id,
        this.template(templateId),
        { user_ids: userIds },
        intersection,
        this.editorsTemplate(templateId),
      );
    },

    checkUpdateTemplateDetails(
      templateId: string,
      payload: UpdateProjectMapTemplateDetailsRequest,
    ) {
      const template = this.templateBasics(templateId);
      const isOwner = template.owner?.id === this.user.id;
      const isOwnerChange =
        !!payload.owner && payload.owner !== template.owner?.id;

      return checkUpdateTemplateDetails(
        this.user.id,
        template,
        payload,
        this.templateLinks,
        this.user.roles,
        isOwner
          ? {
              ownerReader: isOwnerChange
                ? undefined
                : (this.user.scopes.template_reader ?? []),
              previousOwnerReader: this.user.scopes.template_reader ?? [],
            }
          : {},
      );
    },

    checkUpdateTemplate(
      templateId: string,
      payload: UpdateProjectMapTemplateRequest,
    ) {
      return checkUpdateTemplate(
        this.user.id,
        this.template(templateId),
        payload,
        this.template(templateId).workstreams,
        this.editorsTemplate(templateId),
      );
    },

    checkRemoveEditor(templateId: string, editorToRemove: string) {
      return checkRemoveEditor(
        this.user.id,
        this.template(templateId),
        this.editorsTemplate(templateId),
        editorToRemove,
      );
    },

    checkTemplateAddLink(
      templateId: string,
      payload: CreateLinkTemplateRequest,
    ) {
      return checkTemplateAddLink(
        this.user.id,
        this.template(templateId),
        payload,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateUpdateLink(
      templateId: string,
      payload: UpdateLinkTemplateRequest,
      linkIdToBeUpdated: string,
    ) {
      return checkTemplateUpdateLink(
        this.user.id,
        this.template(templateId),
        payload,
        linkIdToBeUpdated,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateRemoveLinkTemplate(
      templateId: string,
      linkIdToBeRemoved: string,
    ) {
      return checkTemplateRemoveLinkTemplate(
        this.user.id,
        this.template(templateId),
        linkIdToBeRemoved,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateAddFunction(
      templateId: string,
      payload: CreateProjectFunctionRequest,
    ) {
      return checkTemplateAddFunction(
        this.user.id,
        this.template(templateId),
        this.editorsTemplate(templateId),
        payload,
      );
    },

    checkTemplateUpdateFunction(
      templateId: string,
      payload: UpdateProjFunctionRequest,
      functionId: string,
    ) {
      return checkTemplateUpdateFunction(
        this.user.id,
        this.template(templateId),
        this.editorsTemplate(templateId),
        payload,
        functionId,
      );
    },

    checkTemplateRemoveFunction(templateId: string, functionId: string) {
      return checkTemplateRemoveFunction(
        this.user.id,
        this.template(templateId),
        this.editorsTemplate(templateId),
        functionId,
      );
    },

    validateMergeFunctionRequest(
      templateId: string,
      targetFunction: MergeFunctionRequest,
      sourceFunctionId: string,
    ) {
      return validateMergeTemplateFunctionRequest(
        this.template(templateId),
        targetFunction,
        sourceFunctionId,
      );
    },

    checkTemplateAddDocument(
      templateId: string,
      payload: CreateDocumentTemplateRequest,
    ) {
      return checkTemplateAddDocument(
        this.user.id,
        this.template(templateId),
        payload,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateUpdateDocument(
      templateId: string,
      docIdToUpdate: string,
      payload: UpdateDocumentTemplateRequest,
    ) {
      return checkTemplateUpdateDocument(
        this.user.id,
        this.template(templateId),
        docIdToUpdate,
        payload,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateRemoveDocument(templateId: string, docIdToRemove: string) {
      return checkTemplateRemoveDocument(
        this.user.id,
        this.template(templateId),
        docIdToRemove,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateAppendWorkstream(
      templateId: string,
      payload: WorkstreamTemplateCreate,
      before?: string,
    ) {
      return checkTemplateAppendWorkstream(
        this.user.id,
        this.template(templateId),
        payload,
        this.editorsTemplate(templateId),
        before,
      );
    },

    checkTemplateUpdateWorkstream(
      templateId: string,
      payload: WorkstreamTemplateUpdate,
      workstreamIdToUpdate: string,
    ) {
      return checkTemplateUpdateWorkstream(
        this.user.id,
        this.template(templateId),
        payload,
        workstreamIdToUpdate,
        this.editorsTemplate(templateId),
      );
    },

    checkTemplateRemoveWorkstream(templateId: string, workstreamId: string) {
      return checkTemplateRemoveWorkstream(
        this.user.id,
        this.template(templateId),
        workstreamId,
        this.editorsTemplate(templateId),
      );
    },

    checkRemoveTemplate(templateId: string) {
      return checkRemoveTemplate(
        this.user.id,
        this.templateBasics(templateId),
        this.user.roles,
      );
    },

    checkUpdateTemplateStatus(templateId: string, status: TemplateStatus) {
      return checkUpdateTemplateStatus(
        this.user.id,
        this.templateBasics(templateId),
        status,
        this.user.roles,
      );
    },

    checkUpdateActivity(
      templateId: string,
      activityId: string,
      updateRequest: ActivityTemplateUpdate,
    ) {
      return checkTemplateUpdateActivity(
        this.user.id,
        this.template(templateId),
        updateRequest,
        this.activity(templateId, activityId) as ActivityTemplate,
        this.editorsTemplate(templateId),
      );
    },

    checkAddCompletionCriterion(
      templateId: string,
      activityId: string,
      index?: number,
    ) {
      const activity = this.activity(templateId, activityId);

      return checkTemplateAddCompletionCriterionToActivityAtIndex(
        this.user.id,
        this.template(templateId),
        activity,
        index || activity.completion_criteria.length,
        this.editorsTemplate(templateId),
      );
    },

    checkUpdateCompletionCriterion(
      templateId: string,
      activityId: string,
      criterionId: string,
      CCPayload: CompletionCriterionTemplateUpdate,
    ) {
      return checkTemplateUpdateCC(
        this.user.id,
        this.template(templateId),
        activityId,
        CCPayload,
        criterionId,
        this.editorsTemplate(templateId),
      );
    },

    checkRemoveCompletionCriterion(
      templateId: string,
      activityId: string,
      criterionId: string,
    ) {
      return checkTemplateRemoveCCFromActivity(
        this.user.id,
        this.template(templateId),
        this.activity(templateId, activityId),
        criterionId,
        this.editorsTemplate(templateId),
      );
    },
  },
});
